// joi-from-form-schema.ts
import Joi, {
  AnySchema,
  ArraySchema,
  BooleanSchema,
  NumberSchema,
  ObjectSchema,
  StringSchema,
} from "joi";
import type {
  FormSchema,
  FormField,
  FormTextField,
  FormTextAreaField,
  FormNumberField,
  FormBooleanField,
  FormSelectField,
  FormRadioField,
  FormDateField,
  FormJsonField,
  FormObjectField,
  FormArrayField,
  FormVisibleIf,
  FormValidator,
} from "./form-schema";

type Values = Record<string, any>;

export type CustomValidatorMap = Record<
  string,
  (value: any, values: Values) => string | null
>;

/** Build a Joi object schema that validates the form's values. */
export function buildJoiSchema(
  schema: FormSchema,
  customValidators?: CustomValidatorMap
): ObjectSchema<any> {
  const shape: Record<string, AnySchema> = {};

  const allFields = schema.sections.flatMap((s) => flattenFields(s.fields));
  for (const f of allFields) {
    shape[f.id] = fieldToJoi(f, customValidators);
  }

  return Joi.object(shape).unknown(false);
}

function fieldToJoi(
  field: FormField,
  customValidators?: CustomValidatorMap
): AnySchema {
  switch (field.kind) {
    case "text": {
      let j: StringSchema = Joi.string();
      const f: FormTextField = field;
      j = applyCommonStringRules(j, f);
      j = applyRequired(j, f) as StringSchema;
      j = applyCustomValidator(j, f, customValidators) as StringSchema;
      return j;
    }
    case "textarea": {
      let j: StringSchema = Joi.string();
      const f: FormTextAreaField = field;
      j = applyCommonStringRules(j, f);
      j = applyRequired(j, f) as StringSchema;
      j = applyCustomValidator(j, f, customValidators) as StringSchema;
      return j;
    }
    case "number": {
      let j: NumberSchema = Joi.number();
      const f: FormNumberField = field;
      j = applyNumberRules(j, f);
      j = applyRequired(j, f) as NumberSchema;
      j = applyCustomValidator(j, f, customValidators) as NumberSchema;
      return j;
    }
    case "boolean": {
      let j: BooleanSchema = Joi.boolean();
      const f: FormBooleanField = field;
      j = applyRequired(j, f) as BooleanSchema;
      j = applyCustomValidator(j, f, customValidators) as BooleanSchema;
      return j;
    }
    case "select": {
      const f: FormSelectField = field;
      const allowed = f.options.map((o) => o.value);
      let j: AnySchema;

      if (f.multiple) {
        if (f.allowCreate !== true) {
          const items: AnySchema = Joi.valid(...allowed);
          j = (Joi.array().items(items).single(false) as ArraySchema).sparse(
            false
          );
        } else {
          j = (Joi.array().single(false) as ArraySchema).sparse(false);
        }
      } else {
        if (f.allowCreate !== true) {
          j = Joi.valid(...allowed).empty("");
        } else {
          j = Joi.string().empty("");
        }
      }

      j = applyRequired(j, f);
      j = applyCustomValidator(j, f, customValidators);
      return j;
    }
    case "radio": {
      const f: FormRadioField = field;
      const allowed = f.options.map((o) => o.value);
      let j: AnySchema = Joi.valid(...allowed).empty("");
      j = applyRequired(j, f);
      j = applyCustomValidator(j, f, customValidators);
      return j;
    }
    case "date": {
      const f: FormDateField = field;
      let j: StringSchema = Joi.string().isoDate().messages({
        "string.isoDate": "Must be an ISO date (YYYY-MM-DD or ISO-8601)",
      });

      if (f.min) {
        j = j.custom((v, helpers) => {
          if (v && typeof v === "string" && v < f.min!) {
            return helpers.error("any.custom", {
              message: `Must be on/after ${f.min}`,
            });
          }
          return v;
        }, "min date") as StringSchema;
      }
      if (f.max) {
        j = j.custom((v, helpers) => {
          if (v && typeof v === "string" && v > f.max!) {
            return helpers.error("any.custom", {
              message: `Must be on/before ${f.max}`,
            });
          }
          return v;
        }, "max date") as StringSchema;
      }

      j = applyRequired(j, f) as StringSchema;
      j = applyCustomValidator(j, f, customValidators) as StringSchema;
      return j;
    }
    case "json": {
      const f: FormJsonField = field;
      let j: AnySchema = Joi.any().custom((v, helpers) => {
        if (v === "" || v === undefined) return v;
        try {
          if (typeof v === "string") {
            JSON.parse(v);
          }
          return v;
        } catch {
          return helpers.error("any.custom", { message: "Must be valid JSON" });
        }
      }, "json validator");
      j = applyRequired(j, f);
      j = applyCustomValidator(j, f, customValidators);
      return j;
    }
    case "object": {
      const f: FormObjectField = field;
      const childShape: Record<string, AnySchema> = {};
      for (const c of f.fields) {
        childShape[c.id] = fieldToJoi(c, customValidators);
      }
      let j: ObjectSchema<any> = Joi.object(childShape);
      j = applyRequired(j, f) as ObjectSchema<any>;
      j = applyCustomValidator(j, f, customValidators) as ObjectSchema<any>;
      return j;
    }
    case "array": {
      const f: FormArrayField = field;
      // Item schema is AnySchema (so we can call .optional() without casting)
      let item: AnySchema = fieldToJoi(f.of, customValidators);
      item = item.optional();

      let j: ArraySchema = Joi.array().items(item);
      if (typeof f.minItems === "number") j = j.min(f.minItems);
      if (typeof f.maxItems === "number") j = j.max(f.maxItems);

      // applyRequired returns AnySchema; re-narrow to ArraySchema safely by re-wrapping
      const requiredApplied = applyRequired(j, f);
      j = Array.isArray((requiredApplied as any)._singleRules)
        ? (requiredApplied as ArraySchema)
        : j; // defensive: Joi keeps ArraySchema after .required/.optional

      j = applyCustomValidator(j, f, customValidators) as ArraySchema;
      return j;
    }
  }
}

/** String rules */
function applyCommonStringRules<T extends { validators?: FormValidator[] }>(
  j: StringSchema,
  f: T
): StringSchema {
  for (const v of f.validators ?? []) {
    if (v.kind === "minLength")
      j = j.min(v.value).messages({ "string.min": v.message || "Too short" });
    if (v.kind === "maxLength")
      j = j.max(v.value).messages({ "string.max": v.message || "Too long" });
    if (v.kind === "regex") {
      try {
        const re = new RegExp(v.value, v.flags);
        j = j
          .pattern(re)
          .messages({ "string.pattern.base": v.message || "Invalid format" });
      } catch {
        // ignore invalid regex at build time
      }
    }
  }
  return j;
}

/** Number rules */
function applyNumberRules<T extends { validators?: FormValidator[] }>(
  j: NumberSchema,
  f: T
): NumberSchema {
  for (const v of f.validators ?? []) {
    if (v.kind === "min")
      j = j.min(v.value).messages({ "number.min": v.message || "Too small" });
    if (v.kind === "max")
      j = j.max(v.value).messages({ "number.max": v.message || "Too large" });
  }
  return j;
}

/** Required/optional */
function applyRequired<T extends { required?: boolean }>(
  j: AnySchema,
  f: T
): AnySchema {
  return f.required ? j.required() : j.optional().allow("", null);
}

/** Custom validator hook */
function applyCustomValidator(
  j: AnySchema,
  f: FormField,
  map?: CustomValidatorMap
): AnySchema {
  const customRule = (f.validators ?? []).find((r) => r.kind === "custom") as
    | { kind: "custom"; name: string; message?: string }
    | undefined;

  if (!customRule || !customRule.name || !map || !map[customRule.name])
    return j;

  return j
    .custom((value, helpers) => {
      const root = (helpers.state.ancestors?.[0] ?? {}) as Values;
      const msg = map[customRule.name](value, root);
      if (msg) return helpers.error("any.custom", { message: msg });
      return value;
    }, `custom:${customRule.name}`)
    .messages({ "any.custom": "{{#message}}" });
}

/** Flatten object fields for top-level shape building */
function flattenFields(fields: FormField[]): FormField[] {
  const out: FormField[] = [];
  for (const f of fields) {
    out.push(f);
    if (f.kind === "object") {
      const obj: FormObjectField = f;
      out.push(...flattenFields(obj.fields));
    }
  }
  return out;
}

/** Same logic as renderer → visibility for a field */
export function evaluateVisibleIf(
  cond: FormVisibleIf | undefined,
  values: Values
): boolean {
  if (!cond) return true;
  if ("all" in cond) return cond.all.every((c) => evaluateVisibleIf(c, values));
  if ("any" in cond) return cond.any.some((c) => evaluateVisibleIf(c, values));
  if ("not" in cond) return !evaluateVisibleIf(cond.not, values);
  const v = values[cond.field];
  if (cond.equals !== undefined) return Object.is(v, cond.equals);
  if (cond.notEquals !== undefined) return !Object.is(v, cond.notEquals);
  if (cond.in) return cond.in.some((x) => Object.is(x, v));
  if (cond.notIn) return !cond.notIn.some((x) => Object.is(x, v));
  return true;
}

/**
 * Filters Joi errors for fields that are currently hidden.
 * (So hidden fields won't block submit.)
 */
export function filterHiddenFieldErrors(
  schema: FormSchema,
  values: Values,
  errors: Joi.ValidationError | undefined
): Joi.ValidationError | undefined {
  if (!errors) return undefined;
  const visible = new Set<string>();
  for (const sec of schema.sections) {
    if (!evaluateVisibleIf(sec.visibleIf, values)) continue;
    for (const f of flattenFields(sec.fields)) {
      if (evaluateVisibleIf(f.visibleIf, values)) visible.add(f.id);
    }
  }
  const details = errors.details.filter((d) => {
    const id = d.path?.[0] as string;
    return visible.has(id); // keep only visible field errors
  });
  if (details.length === 0) return undefined;
  const e = new Joi.ValidationError(errors.message, details, errors._original);
  return e;
}
